import type { Root } from "./prototypeTypes";
import type { MediaRuntimeRef, MountedPlayer } from "./mediaPlayerTypes";
import { getRuntime } from "./runtimeAccessor";
import { optimisticAudioPlayback, resolveAudioPlaybackVisualState, type AudioPlaybackVisualState } from './audioPlaybackOptimisticIntent';
import { PAUSE_ICON_INNER, PLAY_ICON_INNER } from '../../shared/playerIcons';

type ActiveAudioState = AudioPlaybackVisualState;

export function rememberActiveAudioState(root: Root, state: ActiveAudioState): void {
  (root as unknown as { __activeAudioState?: ActiveAudioState }).__activeAudioState = state;
}

export function syncArticleAudioButtonForMedia(root: Root, media: MountedPlayer, visual?: AudioPlaybackVisualState): void {
	const actual = {
		topicId: media.topicId,
		chapterIndex: media.chapterIndex,
		paused: media.element.paused,
		ended: media.element.ended,
	};
	rememberActiveAudioState(root, actual);
	syncArticleAudioButtonState(root, visual ?? actual);
}

export function syncArticleAudioButtonFromRuntime(root: Root): void {
	const state = (root as unknown as { __activeAudioState?: ActiveAudioState }).__activeAudioState;
	const visual = state ? resolveAudioPlaybackVisualState(root, state) : optimisticAudioPlayback(root);
	syncArticleAudioButtonState(root, visual ?? null);
}

export function syncArticleAudioButtonForPlayback(root: Root, state: AudioPlaybackVisualState | null): void {
	syncArticleAudioButtonState(root, state);
}

function syncArticleAudioButtonState(root: Root, state: ActiveAudioState | null): void {
	const doc = root instanceof Document ? root : root.ownerDocument ?? document;
	const button = doc.querySelector<HTMLElement>('#view-article.active #articleAudioBtn') ?? doc.getElementById("articleAudioBtn");
  if (!button) return;
  const rt = runtimeOf(root);
  const sameChapter = Boolean(rt?.activeCourseId && state?.topicId === rt.activeCourseId && state.chapterIndex === rt.activeChapterIndex);
	const isPlaying = sameChapter && state?.paused === false && !state?.ended;
	// The icon carries the play/pause state; the label stays a constant
	// "Listen" so the pill width never jumps on state changes.
	const label = button.querySelector<HTMLElement>(".article-audio-label");
	if (label) label.textContent = "Listen";
	const icon = button.querySelector<HTMLElement>(".article-audio-icon");
	if (icon) icon.innerHTML = isPlaying ? PAUSE_ICON_INNER : PLAY_ICON_INNER;
	button.setAttribute('aria-pressed', String(isPlaying));
	button.setAttribute('aria-label', isPlaying ? 'Pause chapter audio' : 'Play chapter audio');
	button.title = isPlaying ? 'Pause audio' : 'Play audio';
}

function runtimeOf(root: Root): MediaRuntimeRef | null {
  return getRuntime(root) ?? null;
}
